import type { Cell, FruitBehavior, FruitKind } from '../sim/types';

export interface FruitDefinition {
  id: FruitKind;
  name: string;
  plural: string;
  primary: number;
  secondary: number;
  behavior: FruitBehavior;
  traitIcon: string;
  traitLabel: string;
  traitHint: string;
}

export interface FruitWave {
  kind: FruitKind;
  cells: readonly Cell[];
}

const FRUIT_BY_KIND = {
  strawberry: { id: 'strawberry', name: 'Strawberry', plural: 'Strawberries', primary: 0xff557f, secondary: 0x72df94, behavior: 'slow-roam', traitIcon: '↝', traitLabel: 'SLOW DRIFT', traitHint: 'Glides slowly without ever stopping, and stays catchable.' },
  greenApple: { id: 'greenApple', name: 'Green Apple', plural: 'Green Apples', primary: 0xaceb56, secondary: 0x62d57b, behavior: 'stationary', traitIcon: '◆', traitLabel: 'ANCHORED', traitHint: 'Waits on its marked snow tile.' },
  orange: { id: 'orange', name: 'Frost Orange', plural: 'Frost Oranges', primary: 0xffa83d, secondary: 0x66d684, behavior: 'stationary', traitIcon: '◆', traitLabel: 'ANCHORED', traitHint: 'Waits on its marked snow tile.' },
  pear: { id: 'pear', name: 'Snow Pear', plural: 'Snow Pears', primary: 0xc2ed68, secondary: 0x6b4937, behavior: 'runner', traitIcon: '➜', traitLabel: 'QUICK RUNNER', traitHint: 'Dashes between safe cells, then pauses to be intercepted.' },
  cherries: { id: 'cherries', name: 'Cherry Pair', plural: 'Cherry Pairs', primary: 0xff4775, secondary: 0x70d382, behavior: 'teleport', traitIcon: '✦', traitLabel: 'PHASE JUMP', traitHint: 'Warns, vanishes, and reappears on a reachable tile.' },
  grapes: { id: 'grapes', name: 'Aurora Grapes', plural: 'Aurora Grapes', primary: 0x9675ff, secondary: 0x69df9a, behavior: 'stationary', traitIcon: '◆', traitLabel: 'ANCHORED', traitHint: 'Waits on its marked snow tile.' },
  watermelon: { id: 'watermelon', name: 'Moon Melon', plural: 'Moon Melons', primary: 0xff6381, secondary: 0x71df79, behavior: 'stationary', traitIcon: '◆', traitLabel: 'ANCHORED', traitHint: 'Waits on its marked snow tile.' },
  pineapple: { id: 'pineapple', name: 'Star Pineapple', plural: 'Star Pineapples', primary: 0xffcf55, secondary: 0x63cf79, behavior: 'leaf-flight', traitIcon: '⌁', traitLabel: 'LEAF FLIGHT', traitHint: 'Its crown spins before a short untouchable flight.' },
  banana: { id: 'banana', name: 'Crystal Banana', plural: 'Crystal Bananas', primary: 0xffe36e, secondary: 0xa9773e, behavior: 'stationary', traitIcon: '◆', traitLabel: 'STAYS PUT', traitHint: 'Never leaves its starting tile.' },
  blueberries: { id: 'blueberries', name: 'Frost Blueberries', plural: 'Frost Blueberries', primary: 0x6687ff, secondary: 0x85efff, behavior: 'stationary', traitIcon: '◆', traitLabel: 'ANCHORED', traitHint: 'Waits on its marked snow tile.' },
} as const satisfies Readonly<Record<FruitKind, FruitDefinition>>;

const FRUIT_ORDER: readonly FruitKind[] = [
  'strawberry',
  'greenApple',
  'orange',
  'pear',
  'cherries',
  'grapes',
  'watermelon',
  'pineapple',
  'banana',
  'blueberries',
];

export const FRUITS: readonly FruitDefinition[] = FRUIT_ORDER.map((kind) => FRUIT_BY_KIND[kind]);

export function getFruit(kind: FruitKind): FruitDefinition {
  return FRUIT_BY_KIND[kind];
}

export function buildFruitWaves(cells: readonly Cell[], levelId: number, fruitOrder?: readonly FruitKind[]): readonly FruitWave[] {
  if (cells.length === 0) return [];
  const waveCount = Math.min(cells.length, 4, Math.max(1, Math.ceil(cells.length / 3)));
  const baseSize = Math.floor(cells.length / waveCount);
  const largerWaves = cells.length % waveCount;
  const waves: FruitWave[] = [];
  let offset = 0;
  for (let index = 0; index < waveCount; index += 1) {
    const size = baseSize + (index < largerWaves ? 1 : 0);
    const fallback = FRUITS[(levelId - 1 + index) % FRUITS.length]!;
    const kind = fruitOrder?.[index] ?? fallback.id;
    waves.push({ kind, cells: cells.slice(offset, offset + size).map((cell) => ({ ...cell })) });
    offset += size;
  }
  return waves;
}

export function buildDenseFruitWaves(
  routes: readonly (readonly Cell[])[],
  fruitOrder: readonly FruitKind[],
): readonly FruitWave[] {
  if (routes.length === 0) return [];
  return fruitOrder.slice(0, routes.length).map((kind, waveIndex) => ({
    kind,
    cells: (routes[waveIndex] ?? []).map((cell) => ({ ...cell })),
  }));
}
